import React from 'react';
import './StupidSystemDesigns.css';

const amazonLockerDesignCode = String.raw`
#include <iostream>
#include <unordered_set>
#include <unordered_map>
#include <vector>
#include <string>

using namespace std;

enum SlotState { EMPTY, BOOKED };

class Slot {
    int slotId;
    SlotState state;
    string code;
    int packageId;
    int expiryTime;
    
public:
    Slot(int id) {
        slotId = id;
        state = SlotState::EMPTY;
        code = "";
        packageId = -1;
        expiryTime = -1;
    }
    
    bool checkEmpty() {
        return state == SlotState::EMPTY;
    }
    
    void dropPackage(string code, int pId, int time) {
        state = SlotState::BOOKED;
        this->code = code;
        packageId = pId;
        expiryTime = 7 + time; // TTL of 7 days
    }
    
    void pickUpPackage() {
        code = "";
        packageId = -1;
        expiryTime = -1;
        state = SlotState::EMPTY;
    }
    
    int getExpiry() {
        return expiryTime;
    }
    
    int getId() {
        return slotId;
    }
};

class Locker {
    int tick = 0;
    unordered_set<Slot*> emptySlotsSet;
    unordered_map<string, Slot*> bookedSlots;
    int totalSlots, emptySlots;
    int packageGen = 1;

public:
    Locker(int slots) {
        tick = 0;
        totalSlots = slots;
        emptySlots = slots;
        packageGen = 1;
        for (int i = 1; i <= slots; i++) {
            Slot *slot = new Slot(i);
            emptySlotsSet.insert(slot);
        }
    }

    ~Locker() {
        for (auto slot : emptySlotsSet) delete slot;
        for (auto pair : bookedSlots) delete pair.second;
    }

    void updateTime(int clicks) {
        tick += clicks;
        cout << "\n--- Time advanced by " << clicks << " days. Current Day: " << tick << " ---\n";
        cleanupLocker();
    }
    
    string dropPackage() {
        if (emptySlots == 0) {
            cout << "Failed to drop: Locker is full!\n";
            return "";
        }
        
        int packageId = packageGen++;
        string code = "CODE-" + to_string(packageId);
        
        Slot *slot = *emptySlotsSet.begin();
        emptySlotsSet.erase(slot);
        bookedSlots[code] = slot;
        emptySlots--;
        
        slot->dropPackage(code, packageId, tick);
        cout << "Package dropped in Slot " << slot->getId() << ". User pickup code: " << code << "\n";
        return code;
    }

    bool pickupPackage(string code) {
        if (bookedSlots.find(code) == bookedSlots.end()) {
            cout << "Failed to pick up: Invalid or expired code (" << code << ")\n";
            return false;
        }
        
        Slot *slot = bookedSlots[code];
        bookedSlots.erase(code);
        emptySlots++;
        slot->pickUpPackage();
        emptySlotsSet.insert(slot);
        
        cout << "Package successfully picked up using code: " << code << "\n";
        return true;
    } 
    
    void cleanupLocker() {
        vector<string> removedCodes;
        for (auto pairs : bookedSlots) {
            if (pairs.second->getExpiry() < tick) {
                Slot *slot = pairs.second;
                slot->pickUpPackage();
                emptySlotsSet.insert(slot);
                removedCodes.push_back(pairs.first);
                emptySlots++;
            }
        }
        
        for (auto &str : removedCodes) {
            bookedSlots.erase(str);
            cout << "System Cleanup: Removed expired package with code " << str << "\n";
        }
    }
};

// --- DRIVER CODE ---
int main() {
    cout << "Initializing Amazon Locker with 2 slots...\n";
    Locker locker(2);

    // 1. Driver drops packages
    string code1 = locker.dropPackage(); // Should succeed
    string code2 = locker.dropPackage(); // Should succeed
    string code3 = locker.dropPackage(); // Should fail (full)

    // 2. User picks up a package
    cout << "\nUser attempts pickup...\n";
    locker.pickupPackage(code1);         // Should succeed
    locker.pickupPackage("INVALID-99");  // Should fail

    // 3. Driver drops another package in the newly freed slot
    cout << "\nDriver dropping another package...\n";
    string code4 = locker.dropPackage(); // Should succeed

    // 4. Simulate time passing past the 7-day TTL
    locker.updateTime(8); // Advances tick by 8. code2 and code4 should expire.

    // 5. Try to pick up expired package
    cout << "\nUser attempts to pick up expired package...\n";
    locker.pickupPackage(code2); // Should fail

    // 6. Locker should now be completely empty again
    cout << "\nChecking if slots freed up...\n";
    locker.dropPackage();
    locker.dropPackage();
    
    return 0;
}

`;
const trustBasedTubDesignCode = String.raw`
#include <iostream>
#include <vector>
#include <algorithm>

using namespace std;

class TubLocker {
    vector<string> the_tub;

public:
    // O(1) Drop: Just toss it in the tub. No receipts, no tracking.
    void drop(string item) {
        the_tub.push_back(item);
    }

    // O(N) Pickup: Know the magic word? Take literally anything you want.
    string rummage(string code, string desired_package) {
        if (code != "SankKey") 
            return "Tub Lid Locked: *Aggressive Beeping*";

        // Search the tub for the item you want to take
        auto it = find(the_tub.begin(), the_tub.end(), desired_package);
        
        if (it != the_tub.end()) {
            string stolen_goods = *it;
            the_tub.erase(it); // Remove it from the tub
            return "Success: You fished out [" + stolen_goods + "]";
        }
        
        return "Item not found. But the tub is open, just take someone else's stuff.";
    }
};

int main() {
    TubLocker tub;
    
    // Drivers just dumping things
    tub.drop("Bob's iPhone");
    tub.drop("Alice's Books");
    tub.drop("Charlie's PS5");

    // Attackers and honest users
    cout << tub.rummage("WrongKey", "Charlie's PS5") << "\n"; 
    cout << tub.rummage("SankKey", "Charlie's PS5") << "\n"; // Eve steals Charlie's PS5
    cout << tub.rummage("SankKey", "Bob's iPhone") << "\n";  // Bob gets his own phone
    cout << tub.rummage("SankKey", "Charlie's PS5") << "\n"; // Charlie arrives too late

    return 0;
}
`;

function AmazonLockerDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Amazon Locker Design</h1>
                            <p className="system-designs-subtitle">
                                A locker system that gives delivery drivers a temporary place to leave packages and users a code to retrieve them.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Allow delivery drivers to drop packages into available locker slots.</li>
                                <li>Allow users to retrieve packages with a unique code.</li>
                                <li>Find an open slot before storing a package.</li>
                                <li>Generate a unique pickup code for every dropped package.</li>
                                <li>Reset a slot to empty after the package is picked up.</li>
                                <li>Expire packages after a time-to-live of seven days.</li>
                                <li>Use ticks to simulate the passage of time.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Idea</h2>
                            <p className="system-design-description">
                                The locker owns a fixed number of slots and tracks which packages are currently stored. A driver requests an available slot, drops off a package, and receives a unique code that can be sent to the user. The user presents that code to open the matching slot and pick up his package, after which the slot becomes available again.
                            </p>
                            <p className="system-design-description">
                                Each package stores the tick at which it was dropped. Cleanup compares that timestamp with the current tick and removes packages that have been waiting for seven days.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Entities</h2>
                            <h3>LOCKER</h3>
                            <ul>
                                <li><strong>slots:</strong> Collection of <code>SLOT</code> objects.</li>
                                <li><strong>map[uid -&gt; package]:</strong> Maps a package or user identifier to its stored package.</li>
                                <li><strong>total slots:</strong> Total locker capacity.</li>
                                <li><strong>full slots:</strong> Number of slots currently holding packages.</li>
                                <li><strong>drop package:</strong> Find an empty slot, store the package, and create a unique code.</li>
                                <li><strong>pick up:</strong> Find the package by code and release its slot.</li>
                                <li><strong>cleanup old packages:</strong> Remove packages older than seven days.</li>
                            </ul>

                            <h3>SLOT</h3>
                            <ul>
                                <li><strong>state:</strong> <code>empty</code> or <code>booked</code>.</li>
                                <li><strong>code:</strong> Unique code used to retrieve the package.</li>
                                <li><strong>check empty:</strong> Report whether the slot can accept a package.</li>
                                <li><strong>drop package:</strong> Store a package and mark the slot as booked.</li>
                                <li><strong>pick up:</strong> Remove the package, clear the code, and mark the slot as empty.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Time And Expiration</h2>
                            <ul>
                                <li>Maintain a current tick for the locker.</li>
                                <li>Record the drop tick with each package.</li>
                                <li>Advance time by calling a tick operation.</li>
                                <li>During cleanup, expire a package when the current tick is at least seven days after its drop tick.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Alternative: Trust-Based Tub</h2>
                            <p className="system-design-description">
                                This architecture completely abandons the concept of slots, ownership, and unique keys. All packages are dumped into a single bucket, and anyone who knows the master password can go fishing for whatever they want.
                            </p>
                            <ul>
                                <li><strong>One shared tub:</strong> Every package is stored in the same undifferentiated bucket.</li>
                                <li><strong>Master password:</strong> The only access control for every package.</li>
                                <li><strong>No ownership:</strong> The system does not track which user owns which package.</li>
                                <li><strong>No unique keys:</strong> Retrieval depends on manually identifying the package in the tub.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Trust-Based Tub Code</h2>
                            </div>
                            <pre className="design-code-block"><code>{trustBasedTubDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                            </div>
                            <pre className="design-code-block"><code>{amazonLockerDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Boiiis, Want to see something cool? Amazoff.com redirects to Amazon.com. How cool is that? Very cool!
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default AmazonLockerDesign;